import type { CSSVariablesResolver } from "@mantine/core";
import { paletteFor, type Palette } from "./palettes";

// The bridge between the terminal palettes and Mantine.
//
// It emits two families of variables:
//
//   1. --vf-*            the palette itself, consumed by every CSS module.
//   2. --mantine-color-* Mantine's own tokens, re-pointed at the --vf-* set so
//                        stock Mantine components (inputs, menus, tooltips,
//                        tables, drawers…) inherit the terminal look with no
//                        per-usage overrides. This is what lets us stay inside
//                        Mantine's theming system instead of fighting it.
//
// Everything lands in `variables` (scheme-independent) because all ten palettes
// are dark; `dark` carries the handful of tokens Mantine only reads there.
export const vfCssVariablesResolver: CSSVariablesResolver = (theme) => {
  const p: Palette = (theme.other?.palette as Palette | undefined) ?? paletteFor(null);

  const vf = {
    "--vf-bg": p.bg,
    "--vf-panel": p.panel,
    "--vf-panel2": p.panel2,
    "--vf-border": p.border,
    "--vf-border2": p.border2,
    "--vf-hover": p.hover,
    "--vf-text": p.text,
    "--vf-dim": p.dim,
    "--vf-dim2": p.dim2,
    "--vf-sec": p.sec,
    "--vf-acc": p.acc,
    "--vf-acc2": p.acc2,
    "--vf-accbg": p.accbg,
    "--vf-ok": p.ok,
    "--vf-okbd": p.okbd,
    "--vf-okbg": p.okbg,
    "--vf-warn": p.warn,
    "--vf-bad": p.bad,
    "--vf-badbg": p.badbg,
    "--vf-badbd": p.badbd,
    "--vf-scan": p.scan,
  };

  const mantine = {
    // Surfaces and text.
    "--mantine-color-body": p.bg,
    "--mantine-color-text": p.text,
    "--mantine-color-dimmed": p.dim,
    "--mantine-color-bright": p.text,
    // Filled variants paint their label with --mantine-color-white; on these
    // dark palettes the on-accent contrast colour is the page background.
    "--mantine-color-white": p.bg,
    "--mantine-color-black": p.bg,

    // The "default" variant — inputs, subtle buttons, menu/tooltip surfaces.
    "--mantine-color-default": p.panel,
    "--mantine-color-default-hover": p.hover,
    "--mantine-color-default-color": p.text,
    "--mantine-color-default-border": p.border,

    "--mantine-color-placeholder": p.dim2,
    "--mantine-color-anchor": p.acc,
    "--mantine-color-error": p.bad,

    "--mantine-color-disabled": p.panel2,
    "--mantine-color-disabled-color": p.dim2,
    "--mantine-color-disabled-border": p.border2,

    // Primary — everything the mockup paints with `acc`.
    "--mantine-primary-color-filled": p.acc,
    "--mantine-primary-color-filled-hover": p.acc2,
    "--mantine-primary-color-light": p.accbg,
    "--mantine-primary-color-light-hover": p.accbg,
    "--mantine-primary-color-light-color": p.acc,
    "--mantine-primary-color-contrast": p.bg,

    // Semantic aliases used by Badge/Alert/Progress colour props.
    "--mantine-color-vfAcc-filled": p.acc,
    "--mantine-color-vfAcc-filled-hover": p.acc2,
    "--mantine-color-vfAcc-light": p.accbg,
    "--mantine-color-vfAcc-light-hover": p.accbg,
    "--mantine-color-vfAcc-light-color": p.acc,
    "--mantine-color-vfAcc-outline": p.acc,
    "--mantine-color-vfAcc-outline-hover": p.accbg,

    // Overlays match the mockup's rgba(5,5,3,.6-.66) scrims.
    "--mantine-color-overlay": "rgba(5, 5, 3, 0.62)",
  };

  return {
    variables: { ...vf, ...mantine },
    dark: {
      "--mantine-color-dark-filled": p.panel,
      "--mantine-color-dark-light": p.panel2,
      "--mantine-color-dark-outline": p.border,
    },
    light: {},
  };
};
